import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http'; // N'oubliez pas d'importer HttpClientModule dans votre AppModule
import { fromEvent, merge, Observable, of, timer } from 'rxjs';
import { map, switchMap, catchError, debounceTime, startWith } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NetworkStatusService {
  private online$: Observable<boolean>;
  private readonly CHECK_URL = 'https://www.google.com/favicon.ico'; // Une URL légère et fiable
  private readonly CHECK_INTERVAL_MS = 5000; // Vérifie toutes les 5 secondes en cas de doute

  constructor(private http: HttpClient) {
    // Vérification initiale et écoute des événements online/offline
    const initialStatus = of(navigator.onLine);
    const browserStatusChanges = merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    );

    // Combinaison des vérifications du navigateur et de la vérification HTTP active
    this.online$ = merge(initialStatus, browserStatusChanges).pipe(
      debounceTime(300), // Empêche les vérifications trop rapides
      switchMap(browserOnline => {
        if (browserOnline) {
          // Si le navigateur dit qu'on est en ligne, on fait une petite requête pour confirmer
          return this.checkConnectivityWithImage();
        } else {
          // Si le navigateur dit qu'on est hors ligne, on le croit
          return of(false);
        }
      }),
      // Ajoutez une vérification périodique si vous voulez être super robuste
      // et détecter les pertes de connexion sans événements online/offline
      // (par exemple, si le câble est débranché sans que le navigateur ne le détecte immédiatement)
      switchMap(httpOnline => {
        if (httpOnline) {
          return timer(0, this.CHECK_INTERVAL_MS).pipe(
            switchMap(() => this.checkConnectivityWithImage())
          );
        }
        return of(false); // Si déjà hors ligne ou la vérification HTTP échoue, on reste hors ligne
      }),
      startWith(true) // Assumer en ligne au démarrage, sera mis à jour
    );
  }

  private checkConnectivityWithHttp(): Observable<boolean> {
    // Utilise une requête HEAD pour être plus léger
    return this.http.head(this.CHECK_URL, { observe: 'response' }).pipe(
      map(response => response.status >= 200 && response.status < 300), // Succès HTTP
      catchError(() => of(false)) // Échec de la requête
    );
  }

  // Ce n'est PAS avec HttpClient, c'est une méthode alternative si vous ne pouvez pas utiliser votre backend
private checkConnectivityWithImage(): Observable<boolean> {
  return new Observable(observer => {
    const img = new Image();
    img.onload = () => {
      observer.next(true); // Image chargée, donc probablement en ligne
      observer.complete();
    };
    img.onerror = () => {
      observer.next(false); // Échec du chargement, probablement hors ligne ou URL invalide
      observer.complete();
    };
    // Utilisez une URL qui ne déclenchera PAS CORS pour une requête XHR,
    // mais qui peut être chargée via une balise <img>
    img.src = 'https://www.google.com/favicon.ico?' + new Date().getTime(); // Ajout d'un paramètre de cache-busting
  });
}

  get isOnline(): Observable<boolean> {
    return this.online$;
  }
}
